import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

// English translations
import en_common from './locales/en/common'
import en_auth from './locales/en/auth'
import en_properties from './locales/en/properties'
import en_customers from './locales/en/customers'
import en_leases from './locales/en/leases'
import en_meters from './locales/en/meters'
import en_units from './locales/en/units'
import en_templates from './locales/en/templates'
import en_settings from './locales/en/settings'
import en_invoices from './locales/en/invoices'
import en_maintenance from './locales/en/maintenance'
import en_parcels from './locales/en/parcels'
import en_reports from './locales/en/reports'
import en_payments from './locales/en/payments'
import en_parking from './locales/en/parking'
import en_external_parking from './locales/en/external_parking'
import en_expenses from './locales/en/expenses'
import en_timesheet from './locales/en/timesheet'
import en_common_area_fees from './locales/en/common_area_fees'
import en_cmms from './locales/en/cmms'
import en_bank_reconciliation from './locales/en/bank_reconciliation'
import en_accounting from './locales/en/accounting'
import en_announcements from './locales/en/announcements'
import en_debt_collection from './locales/en/debt_collection'
import en_onboarding from './locales/en/onboarding'
import en_microsite from './locales/en/microsite'
import en_contracts from './locales/en/contracts'
import en_assistant from './locales/en/assistant'
import en_incentives from './locales/en/incentives'
import en_shared_owners from './locales/en/shared_owners'
import en_visitors from './locales/en/visitors'
import en_governance from './locales/en/governance'
import en_account from './locales/en/account'

// Thai translations
import th_common from './locales/th/common'
import th_auth from './locales/th/auth'
import th_properties from './locales/th/properties'
import th_customers from './locales/th/customers'
import th_leases from './locales/th/leases'
import th_meters from './locales/th/meters'
import th_units from './locales/th/units'
import th_templates from './locales/th/templates'
import th_settings from './locales/th/settings'
import th_invoices from './locales/th/invoices'
import th_maintenance from './locales/th/maintenance'
import th_parcels from './locales/th/parcels'
import th_reports from './locales/th/reports'
import th_payments from './locales/th/payments'
import th_parking from './locales/th/parking'
import th_external_parking from './locales/th/external_parking'
import th_expenses from './locales/th/expenses'
import th_timesheet from './locales/th/timesheet'
import th_common_area_fees from './locales/th/common_area_fees'
import th_cmms from './locales/th/cmms'
import th_bank_reconciliation from './locales/th/bank_reconciliation'
import th_accounting from './locales/th/accounting'
import th_announcements from './locales/th/announcements'
import th_debt_collection from './locales/th/debt_collection'
import th_onboarding from './locales/th/onboarding'
import th_microsite from './locales/th/microsite'
import th_contracts from './locales/th/contracts'
import th_assistant from './locales/th/assistant'
import th_incentives from './locales/th/incentives'
import th_shared_owners from './locales/th/shared_owners'
import th_visitors from './locales/th/visitors'
import th_governance from './locales/th/governance'
import th_account from './locales/th/account'

const resources = {
  en: {
    common: en_common,
    auth: en_auth,
    properties: en_properties,
    customers: en_customers,
    leases: en_leases,
    meters: en_meters,
    units: en_units,
    templates: en_templates,
    settings: en_settings,
    invoices: en_invoices,
    maintenance: en_maintenance,
    parcels: en_parcels,
    reports: en_reports,
    payments: en_payments,
    parking: en_parking,
    external_parking: en_external_parking,
    expenses: en_expenses,
    timesheet: en_timesheet,
    common_area_fees: en_common_area_fees,
    cmms: en_cmms,
    bank_reconciliation: en_bank_reconciliation,
    accounting: en_accounting,
    announcements: en_announcements,
    debt_collection: en_debt_collection,
    onboarding: en_onboarding,
    microsite: en_microsite,
    contracts: en_contracts,
    assistant: en_assistant,
    incentives: en_incentives,
    shared_owners: en_shared_owners,
    visitors: en_visitors,
    governance: en_governance,
    account: en_account,
  },
  th: {
    common: th_common,
    auth: th_auth,
    properties: th_properties,
    customers: th_customers,
    leases: th_leases,
    meters: th_meters,
    units: th_units,
    templates: th_templates,
    settings: th_settings,
    invoices: th_invoices,
    maintenance: th_maintenance,
    parcels: th_parcels,
    reports: th_reports,
    payments: th_payments,
    parking: th_parking,
    external_parking: th_external_parking,
    expenses: th_expenses,
    timesheet: th_timesheet,
    common_area_fees: th_common_area_fees,
    cmms: th_cmms,
    bank_reconciliation: th_bank_reconciliation,
    accounting: th_accounting,
    announcements: th_announcements,
    debt_collection: th_debt_collection,
    onboarding: th_onboarding,
    microsite: th_microsite,
    contracts: th_contracts,
    assistant: th_assistant,
    incentives: th_incentives,
    shared_owners: th_shared_owners,
    visitors: th_visitors,
    governance: th_governance,
    account: th_account,
  },
}

// Guard the module-level read: some test environments (and SSR) evaluate this
// module before a usable `localStorage` exists. In the browser it is always
// defined, so this only affects non-browser contexts and falls back to 'th'.
export const SUPPORTED_LANGUAGES = ['en', 'th'] as const
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

function isSupportedLanguage(lang: string | null | undefined): lang is SupportedLanguage {
  return lang === 'en' || lang === 'th'
}

/**
 * Resolve the boot language with explicit precedence (B-026a):
 *   1. an explicit user/account choice persisted to localStorage
 *   2. the device/browser locale — so a Thai phone opens in Thai and an English
 *      phone in English (the old code only read localStorage and otherwise
 *      hard-defaulted, with no device detection at all)
 *   3. the app default ('th')
 * The account's own `settings.language` is applied after login by useLanguageSync.
 * Guarded for non-browser/SSR/test contexts. Exported for unit tests.
 */
export function resolveInitialLanguage(): SupportedLanguage {
  const stored =
    typeof localStorage !== 'undefined' ? localStorage.getItem('language') : null
  if (isSupportedLanguage(stored)) return stored

  // Device/browser locales. On iOS WKWebView navigator.language reflects the app's
  // declared localizations (Info.plist CFBundleLocalizations = en, th) — scan the
  // whole ordered navigator.languages list (not just the first) so a supported
  // locale behind an unsupported primary is still honored.
  if (typeof navigator !== 'undefined') {
    const locales =
      navigator.languages && navigator.languages.length > 0
        ? navigator.languages
        : navigator.language
          ? [navigator.language]
          : []
    for (const loc of locales) {
      const code = loc.split('-')[0].toLowerCase()
      if (isSupportedLanguage(code)) return code
    }
  }

  return 'th'
}

const initialLang = resolveInitialLanguage()
document.documentElement.lang = initialLang

i18n.use(initReactI18next).init({
  resources,
  lng: initialLang,
  // Thai-market default: a missing key (or an unsupported active language) falls
  // back to Thai, not English. NB this is the missing-key fallback only — the
  // BOOT language still comes from resolveInitialLanguage (localStorage > device
  // locale > 'th'), so an English-locale device still boots English by design
  // (B-026a). Changing that boot behavior is a separate decision.
  fallbackLng: 'th',
  defaultNS: 'common',
  interpolation: {
    escapeValue: false,
  },
})

// Keep document.documentElement.lang in sync with i18n language changes
i18n.on('languageChanged', (lng: string) => {
  document.documentElement.lang = lng
})

export default i18n
